import { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import {
    Bar, BarChart, CartesianGrid, Cell, ComposedChart, Line,
    ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import s from './AnalyticsTab.module.scss';

// Сетка и оси намеренно бледные: данные должны быть заметнее разметки
const AXIS = { stroke: '#9ca3af', fontSize: 11 };
const GRID = '#eef2f7';
const ACCENT = '#2563eb';

// Семантика стадий B24: P - в работе, S - успешно, F - отказ.
// Красный намеренно не используется: отказ кандидату это штатный исход, а не
// ошибка. Плюс пара зелёный/красный неразличима при дейтеранопии (проверено
// валидатором: ΔE 4.2), а зелёный/синий/янтарный дают 27.4 - успех и отказ
// не сливаются у дальтоников, для дашборда найма это критично
const SEMANTICS = {
    S: { label: 'Успешно', color: '#15803d' },
    P: { label: 'В работе', color: '#2563eb' },
    F: { label: 'Отказ', color: '#b45309' },
    unknown: { label: 'Без стадии', color: '#9ca3af' },
};
const SEMANTIC_ORDER = ['S', 'P', 'F', 'unknown'];

const selectStyles = {
    control: (base) => ({ ...base, minHeight: 34, borderColor: '#e5e7eb', fontSize: 13 }),
    menu: (base) => ({ ...base, fontSize: 13, zIndex: 20 }),
};

function plural(n, one, few, many) {
    const mod10 = n % 10, mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
    return many;
}

const days = (v) => (v === null || v === undefined ? '-' : `${v} ${plural(Math.round(v), 'день', 'дня', 'дней')}`);

/** Плитка с одним числом. Для одиночного показателя диаграмма не нужна -
 *  число само себе визуализация. */
function StatTile({ label, value, hint, big }) {
    return (
        <div className={`${s.tile} ${big ? s.tileBig : ''}`}>
            <div className={s.tileLabel}>{label}</div>
            <div className={s.tileValue}>{value}</div>
            {hint && <div className={s.tileHint}>{hint}</div>}
        </div>
    );
}

/** Всплывающая подсказка. Одна на все графики: разный вид тултипов на соседних
 *  диаграммах заставляет каждый раз заново разбираться, что где написано. */
function ChartTooltip({ active, payload, label, rows }) {
    if (!active || !payload || !payload.length) return null;
    const point = payload[0].payload;
    return (
        <div className={s.tooltip}>
            <div className={s.tooltipTitle}>{point.fullLabel || label}</div>
            {rows(point).filter(Boolean).map((row, i) => (
                <div key={i} className={s.tooltipRow}>
                    <span className={s.tooltipKey}>{row[0]}</span>
                    <span className={s.tooltipValue}>{row[1]}</span>
                </div>
            ))}
        </div>
    );
}

/** Высота графика по числу строк: фиксированная высота либо давит короткие
 *  списки пустотой, либо сплющивает длинные до нечитаемости. */
const chartHeight = (rows) => Math.max(120, rows * 34 + 40);

const shortLabel = (text, limit = 22) =>
    (text || '').length > limit ? `${text.slice(0, limit - 1)}…` : (text || '');

/** Раздел ещё дорабатывается. Говорим об этом сразу и по делу: не «скоро
 *  будет лучше», а что именно значит для того, кто сейчас смотрит цифры. */
function DevBanner() {
    return (
        <div className={s.devBanner}>
            <b>Раздел в разработке.</b> Состав показателей и цифры могут измениться.
        </div>
    );
}

const SEVERITY = {
    problem: { color: '#b91c1c', label: 'Мешает нанимать' },
    attention: { color: '#b45309', label: 'Присмотреться' },
    ok: { color: '#15803d', label: 'В порядке' },
};

/** Разбор от ИИ. Стоит первым на экране сознательно: аналитика нужна ради
 *  вывода, а не ради графиков, и страница должна открываться выводом. */
function Insights({ data, loading, error, onAsk }) {
    if (loading) {
        return <div className={s.insights}><div className={s.insightsIdle}>Читаем цифры...</div></div>;
    }
    if (error) {
        return (
            <div className={s.insights}>
                <div className={s.insightsIdle}>{error}</div>
                <button className={s.buttonGhost} onClick={onAsk}>Повторить</button>
            </div>
        );
    }
    if (!data) {
        return (
            <div className={s.insights}>
                <div className={s.insightsIdle}>
                    Разобрать цифры и подсказать, за что взяться в первую очередь
                </div>
                <button className={s.buttonGhost} onClick={onAsk}>Показать разбор</button>
            </div>
        );
    }
    return (
        <div className={s.insights}>
            <div className={s.insightsSummary}>{data.summary}</div>
            <div className={s.insightsList}>
                {(data.insights || []).filter(i => !i.chart || i.chart === 'general').map((item, i) => {
                    const sev = SEVERITY[item.severity] || SEVERITY.attention;
                    return (
                        <div key={i} className={s.insightItem}>
                            <span className={s.insightDot} style={{ background: sev.color }}
                                  title={sev.label}/>
                            <div>
                                <div className={s.insightTitle}>{item.title}</div>
                                <div className={s.insightDetail}>{item.detail}</div>
                                <div className={s.insightAction}>{item.action}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <button className={s.buttonGhost} onClick={onAsk}>Пересобрать разбор</button>
        </div>
    );
}

/** Что стоит за цифрой: карточки, попавшие в срез. Открывается по клику
 *  на график - «медиана 40 дней» без возможности посмотреть, кто именно там
 *  висит, остаётся числом, с которым нечего делать. */
function DrillPanel({ title, cards, loading, host, onClose }) {
    return (
        <div className={s.drillOverlay} onClick={onClose}>
            <div className={s.drillPanel} onClick={e => e.stopPropagation()}>
                <div className={s.drillHead}>
                    <div className={s.drillTitle}>{title}</div>
                    <button className={s.drillClose} onClick={onClose} aria-label="Закрыть">×</button>
                </div>
                {loading ? (
                    <div className={s.drillEmpty}>Загружаем карточки...</div>
                ) : !cards.length ? (
                    <div className={s.drillEmpty}>Карточек под этот срез нет.</div>
                ) : (
                    <div className={s.drillList}>
                        {cards.map(c => {
                            // Ссылка в сам Битрикс: с дашборда нужно попадать
                            // в карточку, а не переписывать её название в поиск
                            const link = host
                                ? `https://${host}/crm/type/${c.entity_type_id}/details/${c.b24_element_id}/`
                                : null;
                            return (
                                <div key={`${c.entity_type_id}-${c.b24_element_id}`} className={s.drillRow}>
                                    <div className={s.drillMain}>
                                        {link ? (
                                            <a href={link} target="_blank" rel="noreferrer"
                                               className={s.drillLink}>
                                                {c.title || `#${c.b24_element_id}`}
                                            </a>
                                        ) : (
                                            <span>{c.title || `#${c.b24_element_id}`}</span>
                                        )}
                                        <div className={s.drillMeta}>
                                            {c.stage_name || c.stage_id || 'Без стадии'}
                                            {c.assigned_by_name ? ` · ${c.assigned_by_name}` : ''}
                                        </div>
                                    </div>
                                    <div className={s.drillDays}>
                                        {c.days_on_stage != null ? days(c.days_on_stage) : '-'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

/** Наблюдения, относящиеся к одному графику. Стоят под ним, а не общим
 *  списком наверху: разбор рядом с цифрами, о которых он говорит, читается
 *  без перевода взгляда туда-обратно. */
function ChartInsights({ items }) {
    if (!items || !items.length) return null;
    return (
        <div className={s.chartInsights}>
            {items.map((item, i) => {
                const sev = SEVERITY[item.severity] || SEVERITY.attention;
                return (
                    <div key={i} className={s.insightItem}>
                        <span className={s.insightDot} style={{ background: sev.color }}
                              title={sev.label}/>
                        <div>
                            <div className={s.insightTitle}>{item.title}</div>
                            <div className={s.insightDetail}>{item.detail}</div>
                            <div className={s.insightAction}>{item.action}</div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/** Цвет столбца с учётом того, указал ли на него разбор. Подсвеченный
 *  остаётся того же тона, но насыщеннее: другой цвет читался бы как другая
 *  категория данных, а это тот же показатель, просто на него смотрят. */
const barFill = (isTarget, base, highlight) => (isTarget ? highlight : base);

/** График длительности этапов. Вынесен в компонент, потому что рисуется
 *  дважды: этапы кандидатов и этапы вакансий. Смешивать их в одном списке
 *  нельзя - это разные процессы, и «Новая вакансия» рядом с «Собеседованием»
 *  читается как сбой данных. */
function StageDurationChart({ rows, targets, onDrill }) {
    return (
        <ResponsiveContainer width="100%" height={chartHeight(rows.length)}>
            <BarChart
                data={rows.map(r => ({
                    name: shortLabel(r.stage_name || r.stage_id),
                    fullLabel: r.stage_name || r.stage_id || 'Без стадии',
                    stageId: r.stage_id,
                    median: r.median_days,
                    p90: r.p90_days,
                    transitions: r.transitions,
                    now: r.currently_on_stage,
                }))}
                layout="vertical"
                margin={{ top: 4, right: 16, bottom: 4, left: 4 }}
            >
                <CartesianGrid stroke={GRID} horizontal={false}/>
                <XAxis type="number" {...AXIS} tickLine={false} axisLine={false}/>
                <YAxis type="category" dataKey="name" width={150}
                       {...AXIS} tickLine={false} axisLine={false}/>
                <Tooltip
                    cursor={{ fill: '#f8fafc' }}
                    content={<ChartTooltip rows={p => [
                        ['Обычно столько', days(p.median)],
                        p.p90 != null && ['Почти всегда быстрее', days(p.p90)],
                        ['Прошли этап', p.transitions],
                        ['Стоят сейчас', p.now],
                    ]}/>}
                />
                <Bar dataKey="median" radius={[0, 4, 4, 0]} barSize={16} cursor="pointer"
                     onClick={(p) => onDrill(`Этап: ${p.fullLabel}`, p.stageId)}>
                    {rows.map(r => (
                        <Cell key={r.stage_id}
                              fill={barFill(targets[`stage_durations:${r.stage_id}`], ACCENT, '#1e3a8a')}/>
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
}

function Legend({ items }) {
    return (
        <div className={s.legend}>
            {items.map(i => (
                <span key={i.key} className={s.legendItem}>
                    <span className={s.legendSwatch} style={{ background: i.color }}/>
                    {i.label}
                </span>
            ))}
        </div>
    );
}

export default function AnalyticsTab({ api }) {
    const [filters, setFilters] = useState({
        dateFrom: '', dateTo: '', assignedById: null, vacancyId: null,
    });
    const [options, setOptions] = useState({ managers: [], vacancies: [], dataSince: null });
    const [status, setStatus] = useState(null);
    const [data, setData] = useState({
        managerLoad: { rows: [], source: 'current', as_of: null },
        stageDurations: [], timeToHire: null, timeToFill: null, difficulty: [], funnel: [],
        timeline: { bucket: 'month', points: [] },
    });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    // Подсказки не грузим вместе с метриками: вызов платный, пусть его
    // запрашивают осознанно
    const [insights, setInsights] = useState(null);
    const [insightsLoading, setInsightsLoading] = useState(false);
    const [insightsError, setInsightsError] = useState('');
    const [drill, setDrill] = useState(null);          // {title, stageId}
    const [drillCards, setDrillCards] = useState([]);
    const [drillLoading, setDrillLoading] = useState(false);
    const [portalHost, setPortalHost] = useState(null);

    const query = useMemo(() => {
        const p = new URLSearchParams();
        if (filters.dateFrom) p.set('date_from', filters.dateFrom);
        if (filters.dateTo) p.set('date_to', filters.dateTo);
        if (filters.assignedById) p.set('assigned_by_id', filters.assignedById);
        if (filters.vacancyId) p.set('vacancy_id', filters.vacancyId);
        const qs = p.toString();
        return qs ? `?${qs}` : '';
    }, [filters]);

    const load = useCallback(() => {
        setLoading(true);
        setError('');
        return Promise.all([
            api.get(`/analytics/status`),
            api.get(`/analytics/filters`),
            api.get(`/analytics/managers-load${query}`),
            api.get(`/analytics/stage-durations${query}`),
            api.get(`/analytics/time-to-hire${query}`),
            api.get(`/analytics/time-to-fill${query}`),
            api.get(`/analytics/vacancy-difficulty${query}`),
            api.get(`/analytics/funnel${query}`),
            api.get(`/analytics/timeline${query}`),
        ])
            .then(([st, f, ml, sd, tth, ttf, dif, fun, tl]) => {
                setStatus(st.data);
                setOptions({
                    managers: f.data?.managers || [],
                    vacancies: f.data?.vacancies || [],
                    dataSince: f.data?.data_since || null,
                });
                setData({
                    managerLoad: ml.data || { rows: [], source: 'current', as_of: null },
                    stageDurations: sd.data || [],
                    timeToHire: tth.data || null,
                    timeToFill: ttf.data || null,
                    difficulty: dif.data || [],
                    funnel: fun.data || [],
                    timeline: tl.data || { bucket: 'month', points: [] },
                });
            })
            .catch(e => setError(
                e?.response?.data?.detail || 'Не удалось загрузить аналитику. Попробуйте ещё раз.'
            ))
            .finally(() => setLoading(false));
    }, [api, query]);

    useEffect(() => { load(); }, [load]);

    // Хост нужен только чтобы собрать ссылку в карточку Битрикса
    useEffect(() => {
        api.get('/bitrix_portals/portals/current')
            .then(r => setPortalHost(r.data?.host || null))
            .catch(() => {});
    }, [api]);

    const openDrill = useCallback((title, stageId) => {
        setDrill({ title, stageId });
        setDrillLoading(true);
        const p = new URLSearchParams(query.replace(/^\?/, ''));
        if (stageId) p.set('stage_id', stageId);
        const qs = p.toString();
        api.get(`/analytics/cards${qs ? `?${qs}` : ''}`)
            .then(r => setDrillCards(r.data || []))
            .catch(() => setDrillCards([]))
            .finally(() => setDrillLoading(false));
    }, [api, query]);

    // Смена фильтров делает прежний разбор неверным - убираем его,
    // чтобы под новыми цифрами не остался вывод по старым
    useEffect(() => { setInsights(null); setInsightsError(''); }, [query]);

    const askInsights = () => {
        setInsightsLoading(true);
        setInsightsError('');
        api.post(`/analytics/insights${query}`)
            .then(r => setInsights(r.data))
            .catch(e => setInsightsError(
                e?.response?.data?.detail || 'Не удалось получить разбор. Попробуйте позже.'
            ))
            .finally(() => setInsightsLoading(false));
    };

    const refresh = () => {
        setRefreshing(true);
        setNotice('');
        api.post('/analytics/refresh')
            .then(r => {
                const d = r.data || {};
                setNotice(
                    d.has_more
                        ? `Загружено событий: ${d.events}. Данных больше, чем помещается за раз - нажмите ещё раз, чтобы продолжить.`
                        : `Обновлено. Новых событий: ${d.events}, карточек: ${d.items}.`
                );
                return load();
            })
            .catch(e => setError(
                e?.response?.data?.detail || 'Не удалось обновить данные.'
            ))
            .finally(() => setRefreshing(false));
    };

    const managerRows = useMemo(() => {
        const byManager = new Map();
        (data.managerLoad?.rows || []).forEach(row => {
            const key = row.assigned_by_id ?? 'none';
            if (!byManager.has(key)) {
                byManager.set(key, {
                    id: row.assigned_by_id,
                    // Имя приходит из справочника B24; его может не быть -
                    // например, сотрудника удалили. Тогда честнее показать ID,
                    // чем пустую строку
                    name: row.assigned_by_name,
                    total: 0,
                    bySemantic: {},
                });
            }
            const entry = byManager.get(key);
            entry.total += row.items_count;
            const sem = row.stage_semantic_id || 'unknown';
            entry.bySemantic[sem] = (entry.bySemantic[sem] || 0) + row.items_count;
        });
        return [...byManager.values()].sort((a, b) => b.total - a.total);
    }, [data.managerLoad]);

    const durationRows = data.stageDurations.filter(r => r.median_days !== null);
    // Этапы кандидатов и этапы вакансий - разные процессы. В одном списке
    // «Новая вакансия» рядом с «Собеседованием» читается как сбой данных
    const candidateStages = durationRows.filter(r => r.kind !== 'vacancy');
    const vacancyStages = durationRows.filter(r => r.kind === 'vacancy');

    // Предупреждение показываем во всех состояниях, включая загрузку и ошибку:
    // раздел в разработке одинаково верно и когда цифры не отрисовались
    const byChart = useMemo(() => {
        const map = {};
        (insights?.insights || []).forEach(i => {
            (map[i.chart] = map[i.chart] || []).push(i);
        });
        return map;
    }, [insights]);

    const targets = useMemo(() => {
        const map = {};
        (insights?.insights || []).forEach(i => {
            if (i.target_key) map[`${i.chart}:${i.target_key}`] = true;
        });
        return map;
    }, [insights]);

    if (loading) {
        return (
            <div className={s.wrap}>
                <DevBanner/>
                <div className={s.placeholder}>Загрузка аналитики...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={s.wrap}>
                <DevBanner/>
                <div className={s.error}>{error}</div>
                <button className={s.button} onClick={load}>Повторить</button>
            </div>
        );
    }

    const hasData = status?.has_data;

    return (
        <div className={s.wrap}>
            <DevBanner/>
            <div className={s.head}>
                <div>
                    <div className={s.title}>Аналитика найма</div>
                    <div className={s.subtitle}>
                        {status?.last_sync_at
                            ? `Данные на ${new Date(status.last_sync_at).toLocaleString('ru-RU')}`
                            : 'Данные ещё не собирались'}
                        {options.dataSince && ` · история с ${new Date(options.dataSince).toLocaleDateString('ru-RU')}`}
                    </div>
                </div>
                <button className={s.button} onClick={refresh} disabled={refreshing}>
                    {refreshing ? 'Обновляем...' : 'Обновить данные'}
                </button>
            </div>

            {notice && <div className={s.notice}>{notice}</div>}

            {!hasData ? (
                <div className={s.empty}>
                    Данных пока нет. Нажмите «Обновить данные» - мы заберём историю вашей
                    воронки из Битрикс24. Первый сбор берёт год назад и может занять
                    несколько заходов.
                </div>
            ) : (
                <>
                    <div className={s.filters}>
                        <div className={s.filterField}>
                            <label className={s.filterLabel}>Сотрудник</label>
                            <Select
                                styles={selectStyles}
                                isClearable
                                placeholder="Все"
                                options={options.managers.map(m => ({
                                    value: m.id, label: m.name || `ID ${m.id}`,
                                }))}
                                value={(() => {
                                    const m = options.managers.find(x => x.id === filters.assignedById);
                                    return m ? { value: m.id, label: m.name || `ID ${m.id}` } : null;
                                })()}
                                onChange={o => setFilters(f => ({ ...f, assignedById: o?.value ?? null }))}
                            />
                        </div>
                        <div className={s.filterField}>
                            <label className={s.filterLabel}>Вакансия</label>
                            <Select
                                styles={selectStyles}
                                isClearable
                                placeholder="Все"
                                options={options.vacancies.map(v => ({ value: v.id, label: v.title || `#${v.id}` }))}
                                value={(() => {
                                    const v = options.vacancies.find(x => x.id === filters.vacancyId);
                                    return v ? { value: v.id, label: v.title || `#${v.id}` } : null;
                                })()}
                                onChange={o => setFilters(f => ({ ...f, vacancyId: o?.value ?? null }))}
                            />
                        </div>
                        <div className={s.filterField}>
                            <label className={s.filterLabel}>Период с</label>
                            <input
                                type="date" className={s.dateInput} value={filters.dateFrom}
                                onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                            />
                        </div>
                        <div className={s.filterField}>
                            <label className={s.filterLabel}>по</label>
                            <input
                                type="date" className={s.dateInput} value={filters.dateTo}
                                onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                            />
                        </div>
                    </div>

                    <Insights
                        data={insights}
                        loading={insightsLoading}
                        error={insightsError}
                        onAsk={askInsights}
                    />

                    {/* Ключевые числа одной строкой: остальное на экране -
                        подробности к ним, и это должно быть видно по вёрстке */}
                    <div className={s.headline}>
                        <StatTile
                            label="Срок найма"
                            value={days(data.timeToHire?.median_days)}
                            hint="Обычно столько проходит от появления кандидата до найма"
                            big
                        />
                        <StatTile
                            label="Срок закрытия вакансии"
                            value={days(data.timeToFill?.median_days)}
                            hint="Обычно столько вакансия ищет своего человека"
                            big
                        />
                        <StatTile
                            label="Наймов за период"
                            value={data.timeToHire?.hires ?? 0}
                            hint={
                                // Срок считается от даты создания карточки. Если её нет
                                // (карточку удалили), найм в статистику срока не попадает -
                                // молчать об этом нельзя, иначе медиана выглядит враньём
                                data.timeToHire &&
                                data.timeToHire.measured_hires < data.timeToHire.hires
                                    ? `Срок посчитан по ${data.timeToHire.measured_hires} из них`
                                    : 'Кандидатов вышло на работу'
                            }
                            big
                        />
                        <StatTile
                            label="Вакансий в работе"
                            value={data.timeToFill?.open_vacancies ?? 0}
                            hint="Ещё не закрыты"
                            big
                        />
                    </div>

                    <div className={s.sectionLabel}>Динамика</div>

                    <div className={s.card}>
                        <div className={s.cardTitle}>Как меняется найм</div>
                        <div className={s.cardHint}>
                            Наймы и приток кандидатов столбиками, срок найма линией.
                            Шаг {data.timeline.bucket === 'week' ? 'недельный' : 'месячный'} -
                            выбирается по длине периода
                        </div>
                        {data.timeline.points.length < 2 ? (
                            <div className={s.empty}>
                                Для динамики нужно хотя бы два периода с данными.
                                Пока их меньше - смотрите числа выше.
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={260}>
                                <ComposedChart
                                    data={data.timeline.points.map(p => ({
                                        name: p.label,
                                        fullLabel: p.label,
                                        hires: p.hires,
                                        newCandidates: p.new_candidates,
                                        median: p.median_days,
                                    }))}
                                    margin={{ top: 8, right: 8, bottom: 4, left: 0 }}
                                >
                                    <CartesianGrid stroke={GRID} vertical={false}/>
                                    <XAxis dataKey="name" {...AXIS} tickLine={false} axisLine={false}/>
                                    {/* Две оси здесь оправданы: слева люди, справа дни.
                                        Это разные единицы, а не два способа показать одно */}
                                    <YAxis yAxisId="people" {...AXIS} tickLine={false} axisLine={false}/>
                                    <YAxis yAxisId="days" orientation="right" {...AXIS}
                                           tickLine={false} axisLine={false}/>
                                    <Tooltip
                                        cursor={{ fill: '#f8fafc' }}
                                        content={<ChartTooltip rows={p => [
                                            ['Наймов', p.hires],
                                            ['Новых кандидатов', p.newCandidates],
                                            p.median != null && ['Обычно нанимали за', days(p.median)],
                                        ]}/>}
                                    />
                                    <Bar yAxisId="people" dataKey="newCandidates" fill="#bfdbfe"
                                         radius={[4, 4, 0, 0]} barSize={18}/>
                                    <Bar yAxisId="people" dataKey="hires" fill="#15803d"
                                         radius={[4, 4, 0, 0]} barSize={18}/>
                                    <Line yAxisId="days" type="monotone" dataKey="median"
                                          stroke="#b45309" strokeWidth={2} dot={{ r: 3 }}
                                          connectNulls/>
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                        <Legend items={[
                            { key: 'new', label: 'Новые кандидаты', color: '#bfdbfe' },
                            { key: 'hires', label: 'Наймы', color: '#15803d' },
                            { key: 'median', label: 'Срок найма, дни', color: '#b45309' },
                        ]}/>
                        <ChartInsights items={byChart.timeline}/>
                    </div>

                    <div className={s.sectionLabel}>Воронка</div>

                    <div className={s.card}>
                        <div className={s.cardTitle}>Прохождение этапов</div>
                        <div className={s.cardHint}>
                            Сколько кандидатов побывало на каждом этапе, а не сидит там
                            сейчас. Доля считается от всех кандидатов воронки, поэтому
                            этапы не обязаны убывать сверху вниз: в первый этап попадают
                            не все. Порядок этапов взят из Битрикс24
                        </div>
                        {data.funnel.length === 0 ? (
                            <div className={s.empty}>
                                Данных о прохождении этапов пока нет.
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={chartHeight(data.funnel.length)}>
                                <BarChart
                                    data={data.funnel.map(f => ({
                                        name: shortLabel(f.stage_name || f.stage_id),
                                        fullLabel: f.stage_name || f.stage_id,
                                        stageId: f.stage_id,
                                        candidates: f.candidates,
                                        conversion: f.conversion,
                                        dropped: f.dropped,
                                        total: f.total_candidates,
                                        median: f.median_days,
                                    }))}
                                    layout="vertical"
                                    margin={{ top: 4, right: 16, bottom: 4, left: 4 }}
                                >
                                    <CartesianGrid stroke={GRID} horizontal={false}/>
                                    <XAxis type="number" {...AXIS} tickLine={false} axisLine={false}/>
                                    <YAxis type="category" dataKey="name" width={150}
                                           {...AXIS} tickLine={false} axisLine={false}/>
                                    <Tooltip
                                        cursor={{ fill: '#f8fafc' }}
                                        content={<ChartTooltip rows={p => [
                                            ['Кандидатов', p.candidates],
                                            p.conversion != null && ['Доля от всех', `${Math.round(p.conversion * 100)}%`],
                                            p.dropped != null && ['Остановились здесь', p.dropped],
                                            p.median != null && ['Обычно ждут на этапе', days(p.median)],
                                        ]}/>}
                                    />
                                    <Bar dataKey="candidates" radius={[0, 4, 4, 0]}
                                         barSize={16} cursor="pointer"
                                         onClick={(p) => openDrill(`Этап: ${p.fullLabel}`, p.stageId)}>
                                        {data.funnel.map(f => (
                                            <Cell key={f.stage_id}
                                                  fill={barFill(targets[`funnel:${f.stage_id}`], ACCENT, '#1e3a8a')}/>
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                        <ChartInsights items={byChart.funnel}/>
                    </div>

                    <div className={s.sectionLabel}>Сроки подробно</div>

                    <div className={s.card}>
                        <div className={s.cardTitle}>Срок найма кандидата</div>
                        <div className={s.cardHint}>
                            Сколько времени проходит от появления кандидата в базе до
                            того, как его наняли. Считается по каждому кандидату
                            отдельно. В профессиональной литературе это Time to Hire
                        </div>
                        <div className={s.tiles}>
                            <StatTile
                                label="Почти всегда быстрее"
                                value={days(data.timeToHire?.p90_days)}
                                hint="В этот срок укладываются девять кандидатов из десяти"
                            />
                            <StatTile
                                label="От и до"
                                value={`${days(data.timeToHire?.fastest_days)} - ${days(data.timeToHire?.slowest_days)}`}
                                hint="Самый быстрый и самый долгий наём за период"
                            />
                        </div>
                    </div>

                    <div className={s.card}>
                        <div className={s.cardTitle}>Срок закрытия вакансии</div>
                        <div className={s.cardHint}>
                            Сколько вакансия остаётся открытой: от её создания до
                            найма первого человека. Если по вакансии берут нескольких,
                            срок считается по первому. В профессиональной литературе
                            это Time to Fill
                        </div>
                        <div className={s.tiles}>
                            <StatTile
                                label="Почти всегда быстрее"
                                value={days(data.timeToFill?.p90_days)}
                                hint="В этот срок укладываются девять вакансий из десяти"
                            />
                            <StatTile
                                label="Закрыто за период"
                                value={data.timeToFill?.filled ?? 0}
                                hint="Вакансий, по которым кто-то вышел"
                            />
                        </div>
                        {/* Пустой дашборд из-за незаполненного поля выглядит поломкой -
                            объясняем причину прямо здесь */}
                        {data.timeToFill?.candidates_without_vacancy > 0 && (
                            <div className={s.notice} style={{ marginTop: '12px', marginBottom: 0 }}>
                                У {data.timeToFill.candidates_without_vacancy} кандидатов не
                                заполнено поле вакансии - такие кандидаты не закрывают вакансию,
                                и в этой метрике они не учитываются.
                            </div>
                        )}
                    </div>

                    <div className={s.card}>
                        <div className={s.cardTitle}>Сколько ждут на этапах</div>
                        <div className={s.cardHint}>
                            Сколько кандидат обычно ждёт на каждом этапе. Те, кто
                            стоит на этапе прямо сейчас, в расчёт не входят - их срок
                            ещё не закончился, и они показаны отдельно
                        </div>
                        {candidateStages.length === 0 && vacancyStages.length === 0 ? (
                            <div className={s.empty}>
                                Пока нет ни одного завершённого перехода между стадиями.
                            </div>
                        ) : (
                            <>
                                {candidateStages.length > 0 && (
                                    <StageDurationChart rows={candidateStages} targets={targets}
                                                        onDrill={openDrill}/>
                                )}
                                {vacancyStages.length > 0 && (
                                    <>
                                        <div className={s.subLabel}>Этапы самих вакансий</div>
                                        <div className={s.cardHint}>
                                            Это жизненный цикл вакансии, а не путь кандидата:
                                            сколько она висит на каждом своём этапе
                                        </div>
                                        <StageDurationChart rows={vacancyStages} targets={targets}
                                                            onDrill={openDrill}/>
                                    </>
                                )}
                            </>
                        )}
                        <ChartInsights items={byChart.stage_durations}/>
                    </div>

                    <div className={s.card}>
                        <div className={s.cardTitle}>Сложность найма по вакансиям</div>
                        <div className={s.cardHint}>
                            Насколько тяжело даётся вакансия: расход воронки, срок,
                            доля отказов, качество потока и возвраты кандидатов назад.
                            Это <b>ранг среди ваших же вакансий</b>, а не абсолютная
                            величина - с другим порталом такие числа сравнивать нельзя
                            {data.difficulty.length < 4 && data.difficulty.length > 0 &&
                                '. Вакансий пока мало, поэтому разброс крайний'}
                        </div>
                        {data.difficulty.length === 0 ? (
                            <div className={s.empty}>Вакансий под выбранные фильтры нет.</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={chartHeight(data.difficulty.length)}>
                                <BarChart
                                    data={data.difficulty.map(v => ({
                                        name: shortLabel(v.title || `#${v.vacancy_id}`),
                                        fullLabel: `${v.title || `#${v.vacancy_id}`}${v.is_open ? ' (в работе)' : ''}`,
                                        difficulty: v.difficulty,
                                        candidates: v.candidates,
                                        hires: v.hires,
                                        duration: v.duration_days,
                                        rejection: v.rejection_rate,
                                        score: v.median_score,
                                        backtracks: v.backtracks,
                                        isOpen: v.is_open,
                                    }))}
                                    layout="vertical"
                                    margin={{ top: 4, right: 16, bottom: 4, left: 4 }}
                                >
                                    <CartesianGrid stroke={GRID} horizontal={false}/>
                                    <XAxis type="number" domain={[0, 100]} {...AXIS}
                                           tickLine={false} axisLine={false}/>
                                    <YAxis type="category" dataKey="name" width={150}
                                           {...AXIS} tickLine={false} axisLine={false}/>
                                    <Tooltip
                                        cursor={{ fill: '#f8fafc' }}
                                        content={<ChartTooltip rows={p => [
                                            ['Насколько тяжело, из 100', p.difficulty],
                                            ['Кандидатов', p.candidates],
                                            ['Наймов', p.hires],
                                            p.duration != null && [p.isOpen ? 'Открыта' : 'Закрыта за', days(p.duration)],
                                            p.rejection != null && ['Доля отказов', `${Math.round(p.rejection * 100)}%`],
                                            p.score != null && ['Обычная оценка кандидата', p.score],
                                            p.backtracks > 0 && ['Возвратов назад', p.backtracks],
                                        ]}/>}
                                    />
                                    <Bar dataKey="difficulty" radius={[0, 4, 4, 0]} barSize={16}>
                                        {/* Незакрытые приглушены: они в работе, и их индекс
                                            ещё может измениться */}
                                        {data.difficulty.map(v => (
                                            <Cell key={v.vacancy_id}
                                                  fill={barFill(
                                                      targets[`difficulty:${v.vacancy_id}`],
                                                      v.is_open ? '#93c5fd' : ACCENT,
                                                      '#1e3a8a',
                                                  )}/>
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                        <ChartInsights items={byChart.difficulty}/>
                    </div>

                    <div className={s.card}>
                        <div className={s.cardTitle}>Загрузка сотрудников</div>
                        <div className={s.cardHint}>
                            {/* Свежесть подписываем честно: без периода это состояние
                                на момент последнего обновления, а не «прямо сейчас» */}
                            {data.managerLoad?.source === 'snapshot'
                                ? `Как было на ${new Date(data.managerLoad.as_of).toLocaleDateString('ru-RU')} - последний день выбранного периода, по которому есть данные`
                                : data.managerLoad?.as_of
                                    ? `Сколько карточек у кого по данным на ${new Date(data.managerLoad.as_of).toLocaleString('ru-RU')}`
                                    : 'Сколько карточек у кого'}
                        </div>
                        {/* Период выбран, но слепков за него нет - показываем текущее
                            и говорим об этом, иначе цифры выдадут себя за прошлые */}
                        {data.managerLoad?.source === 'current' && (filters.dateFrom || filters.dateTo) && (
                            <div className={s.notice} style={{ marginBottom: '12px' }}>
                                {filters.vacancyId
                                    ? 'Загрузка за период не разбивается по вакансиям - показано текущее состояние по выбранной вакансии.'
                                    : 'За выбранный период данных о загрузке ещё нет - они копятся с момента подключения аналитики. Показано текущее состояние.'}
                            </div>
                        )}
                        <Legend items={SEMANTIC_ORDER.map(k => ({ key: k, ...SEMANTICS[k] }))}/>
                        {managerRows.length === 0 ? (
                            <div className={s.empty}>Нет карточек под выбранные фильтры.</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={chartHeight(managerRows.length)}>
                                <BarChart
                                    data={managerRows.map(row => ({
                                        name: shortLabel(row.name || (row.id ? `ID ${row.id}` : 'Без ответственного')),
                                        fullLabel: row.name || (row.id ? `ID ${row.id}` : 'Без ответственного'),
                                        total: row.total,
                                        ...Object.fromEntries(
                                            SEMANTIC_ORDER.map(k => [k, row.bySemantic[k] || 0])
                                        ),
                                    }))}
                                    layout="vertical"
                                    margin={{ top: 4, right: 16, bottom: 4, left: 4 }}
                                >
                                    <CartesianGrid stroke={GRID} horizontal={false}/>
                                    <XAxis type="number" {...AXIS} tickLine={false} axisLine={false}/>
                                    <YAxis type="category" dataKey="name" width={150}
                                           {...AXIS} tickLine={false} axisLine={false}/>
                                    <Tooltip
                                        cursor={{ fill: '#f8fafc' }}
                                        content={<ChartTooltip rows={p => [
                                            ['Всего карточек', p.total],
                                            ...SEMANTIC_ORDER
                                                .filter(k => p[k])
                                                .map(k => [SEMANTICS[k].label, p[k]]),
                                        ]}/>}
                                    />
                                    {SEMANTIC_ORDER.map((k, i) => (
                                        <Bar key={k} dataKey={k} stackId="load"
                                             fill={SEMANTICS[k].color} barSize={16}
                                             radius={i === SEMANTIC_ORDER.length - 1 ? [0, 4, 4, 0] : 0}/>
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                        <ChartInsights items={byChart.manager_load}/>
                    </div>
                </>
            )}

            {drill && (
                <DrillPanel
                    title={drill.title}
                    cards={drillCards}
                    loading={drillLoading}
                    host={portalHost}
                    onClose={() => setDrill(null)}
                />
            )}
        </div>
    );
}
